// third-party
import { generate } from '@ant-design/colors';

// ==============================|| PRESET THEME - PRIVACYPAL BRAND ||============================== //
//
// PrivacyPal brand palette (AirOps brand kit #30260 — "Core" + "Retro Accent"):
//   Signal Teal   #028391  → primary / CTA / interactive
//   Deep Navy     #01204E  → text, dark surfaces (never pure black)
//   Forest Sage   #4DA394  → success / trust / positive states
//   Muted Coral   #D54751  → error / risk / urgency
//   Burnt Tangerine #FAA968 → warning / coverage accent
//   Espresso      #503D2D  → grounding / secondary
//   Soft Ivory    #F0ECC9 / Warm Amber #F6DCAC → background washes only
//
// @ant-design/colors `generate(base)` returns a 10-step ramp (index 0 lightest → 9 darkest)
// with the input color at index 5. We pin index 5 to the exact brand hex so `main` is precise.

export const PP_BRAND = {
  teal: '#028391',
  navy: '#01204E',
  sage: '#4DA394',
  coral: '#D54751',
  tangerine: '#FAA968',
  amber: '#F6DCAC',
  ivory: '#F0ECC9',
  espresso: '#503D2D'
};

// PrivacyPal Max gold accent (EPIC-07). Scoped accent tokens, not a theme
// rewrite: gold marks Max-tier badges, borders, and line items only. The
// product still reads as PrivacyPal (teal/navy carry the UI).
export const PP_MAX = {
  gold: '#C9A227', // borders, icons, small marks
  goldDark: '#8A6D1C', // text on light surfaces (readable on white/ivory)
  goldLight: '#E7CF8C', // highlights on dark surfaces
  goldBg: '#FBF6E7' // subtle wash behind badges and rows
};

const DARK_BG = '#0e1b30'; // keep in lockstep with buildDarkGrey()[0] (dark paper)

function ramp(base, mode = 'light') {
  if (mode === 'dark') {
    // @ant-design/colors dark algorithm: washes (low indexes) become deep
    // tinted surfaces instead of near-white, so `lighter`/`100` slots used as
    // chip/tile backgrounds stay readable on dark paper.
    const r = generate(base, { theme: 'dark', backgroundColor: DARK_BG });
    r[5] = base; // pin `main` to the exact brand color
    return r;
  }
  const r = generate(base);
  r[5] = base; // pin `main` to the exact brand color
  return r;
}

function toSlots(r, contrastText) {
  return {
    lighter: r[0],
    100: r[1],
    200: r[2],
    light: r[3],
    400: r[4],
    main: r[5],
    dark: r[6],
    700: r[7],
    darker: r[8],
    900: r[9],
    contrastText
  };
}

export default function Default(colors, presetColor, mode = 'light') {
  const { grey } = colors;
  const greyColors = {
    0: grey[0],
    50: grey[1],
    100: grey[2],
    200: grey[3],
    300: grey[4],
    400: grey[5],
    500: grey[6],
    600: grey[7],
    700: grey[8],
    800: grey[9],
    900: grey[10],
    A50: grey[15],
    A100: grey[11],
    A200: grey[12],
    A400: grey[13],
    A700: grey[14],
    A800: grey[16]
  };
  const contrastText = '#fff';

  const tealRamp = ramp(PP_BRAND.teal, mode);
  const sageRamp = ramp(PP_BRAND.sage, mode);
  const coralRamp = ramp(PP_BRAND.coral, mode);
  const tangerineRamp = ramp(PP_BRAND.tangerine, mode);

  if (mode === 'dark') {
    // Deep Navy is the dark surface itself, so "secondary" flips to a steel
    // blue that reads on it. Warning (light tangerine) takes dark contrast text.
    const steel = {
      lighter: greyColors[100],
      100: greyColors[200],
      200: greyColors[300],
      light: greyColors[400],
      400: greyColors[500],
      main: greyColors[600],
      600: '#a4b3d1',
      dark: greyColors[700],
      800: greyColors[800],
      darker: greyColors[900],
      A100: greyColors[0],
      A200: greyColors.A400,
      A300: greyColors.A700,
      contrastText: greyColors.A50
    };
    return {
      primary: toSlots(tealRamp, contrastText),
      secondary: steel,
      error: toSlots(coralRamp, contrastText),
      warning: { ...toSlots(tangerineRamp, greyColors.A50) },
      info: toSlots(tealRamp, contrastText),
      success: toSlots(sageRamp, contrastText),
      grey: greyColors
    };
  }

  const navyRamp = ramp(PP_BRAND.navy);

  return {
    // Signal Teal — the "PrivacyPal is here" color
    primary: toSlots(tealRamp, contrastText),
    // Deep Navy — authoritative dark accent (used for headers, emphasis)
    secondary: {
      lighter: navyRamp[0],
      100: navyRamp[1],
      200: navyRamp[2],
      light: navyRamp[3],
      400: navyRamp[4],
      main: PP_BRAND.navy,
      600: navyRamp[6],
      dark: navyRamp[7],
      800: navyRamp[8],
      darker: navyRamp[9],
      A100: greyColors[0],
      A200: greyColors.A400,
      A300: greyColors.A700,
      contrastText
    },
    error: toSlots(coralRamp, contrastText),
    warning: { ...toSlots(tangerineRamp, greyColors[900]) },
    info: toSlots(tealRamp, contrastText),
    success: toSlots(sageRamp, contrastText),
    grey: greyColors
  };
}
